import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { TreeView, TreeItem } from '@mui/lab'
import { NavigateNext } from '@mui/icons-material'
import { type OrganizationHierarchy, type OrganizationNameIdChildren } from 'services'
import {
  cardStyles, childrenOrgsTreeView,
  hierarchyOrgLabel,
  parentOrgsContainer,
  skeletonBlockStyles, skeletonLineStyles,
  treeNodeLabel
} from './organization-hierarchy-styles'
import { useSessionContext } from '../../../../../session'

const renderTree = (nodes: OrganizationNameIdChildren): JSX.Element => {
  const { openNewTab } = useSessionContext()

  return (
        <TreeItem
            key={nodes.id}
            nodeId={String(nodes.id)}
            label={
                <Box onClick={() => { openNewTab(`/editOrganization/${nodes.id}`) }} sx={treeNodeLabel}>
                    {nodes.organization_name}
                </Box>
            }
        >
            {Array.isArray(nodes.children) ? nodes.children.map((node) => renderTree(node)) : null}
        </TreeItem>
  )
}
export interface OrganizationHierarchyProps {
  orgId: number
  parentId?: number
  hierarchy?: OrganizationHierarchy
}
export const OrganizationHierarchyBlock: React.FC<OrganizationHierarchyProps> = ({ orgId, hierarchy, parentId = 0 }) => {
  const [expanded, setExpanded] = useState(true)
  const [parentOrgs, setParentOrgs] = useState<OrganizationNameIdChildren[]>([])
  const [childOrgs, setChildOrgs] = useState<OrganizationNameIdChildren[]>([])
  const [loading, setLoading] = useState(true)
  const { openNewTab } = useSessionContext()

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true)
        if (hierarchy != null) {
          setParentOrgs(hierarchy.upper_organizations)
          setChildOrgs(hierarchy.lower_organizations)
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }

    void fetchData()
  }, [orgId, parentId, hierarchy])

  const handleAccordionChange = (): void => {
    setExpanded(!expanded)
  }

  const renderHierarchy = (orgs: OrganizationNameIdChildren[]): React.ReactNode => {
    if (loading) {
      return (<Skeleton variant="rectangular" sx={skeletonLineStyles} />)
    }
    const hierarchy: JSX.Element[] = []

    const renderOrg = (org: OrganizationNameIdChildren): void => {
      if (org.children != null) {
        org.children.forEach((child, index) => {
          renderOrg(child)
        })
      }
      hierarchy.push(
                <Box key={`org-${org.id}`} onClick={() => { openNewTab(`/editOrganization/${org.id}`) }} sx={hierarchyOrgLabel}>
                    {org.organization_name}
                </Box>
      )
    }

    orgs.forEach((org, index) => {
      renderOrg(org)
      if (index < orgs.length - 1) {
        hierarchy.push(<NavigateNext key={`icon-${org.id}`} />)
      }
    })

    return hierarchy
  }

  function getTreeView (): React.ReactNode {
    if (loading) { return (<Skeleton variant="rectangular" sx={skeletonBlockStyles} />) }

    return <TreeView
            defaultCollapseIcon={<ExpandLessIcon/>}
            defaultExpandIcon={<ExpandMoreIcon/>}
            sx={childrenOrgsTreeView}
        >
            {childOrgs.map((node) => renderTree(node))}
        </TreeView>
  }

  return (
        <Card sx={cardStyles}>
            <Accordion expanded={expanded} onChange={handleAccordionChange}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                    <Typography variant="h6">Organization Hierarchy</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <CardContent>
                        <Typography variant="h6">Parent Orgs:</Typography>
                        <Box sx={parentOrgsContainer}>
                            {renderHierarchy(parentOrgs)}
                        </Box>
                        <Typography variant="h6">Children Orgs:</Typography>
                        {getTreeView()}
                    </CardContent>
                </AccordionDetails>
            </Accordion>
        </Card>
  )
}
